import { useEffect, useRef, useState } from "react";
import { api, API } from "../lib/api";
import { toast } from "sonner";
import { UploadCloud, RotateCcw, Loader2, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import { setSiteLogo } from "./SiteLogo";

// Admin > Páginas: logo do site (cabeçalho e rodapé de todas as páginas).
const abs = (u) => (u && u.startsWith("/api/") ? `${API}${u.replace(/^\/api/, "")}` : u);

export function SiteLogoCard() {
  const [saved, setSaved] = useState(null); // null = carregando
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(() => { try { return localStorage.getItem("lgfx_logo_card_open") === "1"; } catch { return false; } });
  const toggle = () => setOpen((o) => { try { localStorage.setItem("lgfx_logo_card_open", o ? "0" : "1"); } catch {} return !o; });
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/site/branding").then(({ data }) => { setSaved(data.logo_url || ""); setUrl(data.logo_url || ""); }).catch(() => setSaved(""));
  }, []);

  const save = async (value) => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/site-branding", { logo_url: value });
      setSaved(data.logo_url); setUrl(data.logo_url); setSiteLogo(data.logo_url);
      toast.success(data.logo_url ? "Logo atualizado" : "Logo padrão restaurado");
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao salvar o logo"); }
    finally { setBusy(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    const form = new FormData(); form.append("file", file); setBusy(true);
    try {
      const { data } = await api.post("/admin/pages/upload-image", form, { headers: { "Content-Type": "multipart/form-data" } });
      await save(data.url);
    } catch (e) { toast.error(e.response?.data?.detail || "Falha no upload"); setBusy(false); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const preview = url ? abs(url) : "/logo-full.png";
  const dirty = saved !== null && url !== saved;

  return (
    <div className="rounded-md border border-border/60 bg-card p-4" data-testid="site-logo-card">
      <button type="button" onClick={toggle} aria-expanded={open} className="w-full flex items-center justify-between gap-3 text-left" data-testid="site-logo-toggle">
        <div className="min-w-0">
          <div className="font-display font-bold text-sm">Logo do site</div>
          {open && <div className="text-[11px] text-muted-foreground">Aparece no cabeçalho e no rodapé de todas as páginas. PNG, JPG, WEBP ou GIF; de preferência com fundo transparente.</div>}
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {saved === null ? <Loader2 className="h-4 w-4 animate-spin text-primary" />
            : !open && <img src={preview} alt="" className="h-6 max-w-[120px] object-contain" />}
          <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && <div className="mt-3 flex flex-col md:flex-row gap-4 md:items-center">
        <div className="flex gap-2 flex-shrink-0">
          <div className="h-16 w-48 rounded-sm border border-border bg-[#0a0a0c] flex items-center justify-center p-2" title="Prévia">
            <img src={preview} alt="Logo atual" className="max-h-full max-w-full object-contain" data-testid="site-logo-preview" />
          </div>
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" className="rounded-sm h-8" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="site-logo-upload">
              {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <UploadCloud className="h-3.5 w-3.5 mr-1.5" />} Enviar logo
            </Button>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="site-logo-file" />
            {saved && (
              <Button size="sm" variant="ghost" className="rounded-sm h-8 text-muted-foreground" disabled={busy} onClick={() => save("")} data-testid="site-logo-reset">
                <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Restaurar padrão
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <input className="flex-1 min-w-0 h-8 bg-input border border-border rounded-sm px-2 text-xs outline-none focus:border-primary"
              placeholder="ou cole a URL de uma imagem" value={url && !url.startsWith("/api/") && !url.startsWith("data:") ? url : ""}
              onChange={(e) => setUrl(e.target.value)} data-testid="site-logo-url" />
            {dirty && <Button size="sm" variant="secondary" className="rounded-sm h-8" disabled={busy} onClick={() => save(url.trim())}>Salvar</Button>}
          </div>
        </div>
      </div>}
    </div>
  );
}

export default SiteLogoCard;
